import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Search, MapPin, CheckCircle2, ArrowLeft, Building2, FileText,
    Loader2, CalendarDays, List, ChevronLeft, ChevronRight, Lock, Download, GraduationCap,
} from "lucide-react";
import axios from "axios";
import StatusBadge, { STATUS_COLORS } from "@/components/StatusBadge";
import MapPanel from "@/components/MapPanel";
import TourPlayer from "@/components/TourPlayer";
import { TOURS } from "@/lib/tours";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["All", "Submitted", "In Review", "Pending", "On Hold", "Approved", "Rejected"];
const TYPES = ["All types", "Pre-Consultation Request", "Official Plan Amendment", "Zoning By-law Amendment", "Draft Plan of Subdivision", "Draft Plan of Condominium", "Site Plan Control", "Consent (Severance)", "Minor Variance", "Part Lot Control"];

const fmt = (n) => `$${(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;
const dstr = (d) => (d ? new Date(d).toLocaleDateString(document.documentElement.lang === "fr" ? "fr-CA" : "en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");

function MiniCalendar({ hearings, onPick }) {
    const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
    const byDay = useMemo(() => {
        const m = {};
        hearings.forEach((h) => { const k = h.date.slice(0, 10); (m[k] = m[k] || []).push(h); });
        return m;
    }, [hearings]);
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    return (
        <div data-testid="hearings-calendar" className="bg-white rounded-[16px] border border-slate-200/70 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <button data-testid="cal-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-[8px] hover:bg-slate-100 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <div className="font-heading font-bold">{cursor.toLocaleDateString(document.documentElement.lang === "fr" ? "fr-CA" : "en-CA", { month: "long", year: "numeric" })}</div>
                <button data-testid="cal-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-[8px] hover:bg-slate-100 transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 mb-1">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                    const has = byDay[key];
                    return (
                        <button key={i} data-testid={has ? `cal-day-${key}` : undefined} onClick={() => has && onPick(byDay[key])}
                            className={`aspect-square rounded-[8px] text-xs font-semibold flex items-center justify-center transition-all ${has ? "bg-[#FFB703]/15 text-[#8a6a08] hover:bg-[#FFB703]/30 ring-1 ring-[#FFB703]/40 cursor-pointer" : "text-slate-400"}`}>
                            {d}
                        </button>
                    );
                })}
            </div>
            <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-[#FFB703]/40" /> {tr("Public hearing scheduled")}</div>
        </div>
    );
}

export default function Portal() {
    const { t } = useLanguage();
    const [params, setParams] = useSearchParams();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const [statusF, setStatusF] = useState("All");
    const [typeF, setTypeF] = useState("All types");
    const [home, setHome] = useState(null);
    const [hearView, setHearView] = useState("list");
    const [calPick, setCalPick] = useState(null);
    const [paymentMsg, setPaymentMsg] = useState("");
    const [tourOpen, setTourOpen] = useState(false);

    const search = async (q = query) => {
        const { data } = await axios.get(`${API}/portal/search`, { params: { query: q } });
        setResults(data);
    };

    useEffect(() => {
        search("");
        axios.get(`${API}/portal/home`).then((r) => setHome(r.data)).catch(() => {});
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (params.get("payment") === "success") {
            setPaymentMsg("Payment confirmed. The planner has been notified in real time.");
            setParams({});
        } else if (params.get("payment") === "cancelled") {
            setPaymentMsg("Payment cancelled. No charge was made.");
            setParams({});
        }
        // eslint-disable-next-line
    }, []);

    const filtered = useMemo(() => results.filter((a) =>
        (statusF === "All" || a.status === statusF) && (typeF === "All types" || a.type === typeF)
    ), [results, statusF, typeF]);

    const openApplication = async (a) => {
        const { data } = await axios.get(`${API}/portal/applications/${a.file_number}`);
        setSelected(data);
    };

    const hearings = home?.hearings || [];

    return (
        <div className="min-h-screen bg-[#E9EFF3] text-[#0A2540]" data-testid="portal-page">
            <nav className="bg-white/80 backdrop-blur-xl sticky top-0 z-40 border-b border-white/60 shadow-[0_2px_20px_-8px_rgba(10,37,64,0.12)]">
                <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5">
                        <img src="/assets/logo.png" alt="LandLogic NexusOne" className="h-10 w-auto" />
                        <span className="text-xs font-bold text-[#0092B0] border border-[#00B4D8]/30 bg-[#00B4D8]/8 rounded-full px-2.5 py-1 uppercase tracking-[0.15em] text-[10px]">{tr("Public Register")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button data-testid="portal-tour-btn" onClick={() => setTourOpen(true)} title={tr("How to use this register")}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-[#0092B0] transition-colors px-2 py-2">
                            <GraduationCap className="w-3.5 h-3.5" /> {t("common.tour")}
                        </button>
                        <LangSwitcher testid="portal-lang-switcher" />
                        <Link to="/" data-testid="portal-back-home" className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-[#0092B0] transition-colors px-2 py-2">
                            <ArrowLeft className="w-3.5 h-3.5" /> {tr("Main site")}
                        </Link>
                        <Link to="/login" data-testid="portal-login-link" className="text-xs font-semibold text-slate-400 hover:text-[#0092B0] transition-colors px-2 py-2">{tr("Staff")}</Link>
                        <Link to="/developer" data-testid="portal-developer-signin" className="brand-btn px-4 py-2 rounded-[8px] text-sm font-bold">{tr("Applicant sign in")}</Link>
                    </div>
                </div>
            </nav>

            {paymentMsg && (
                <div className="max-w-6xl mx-auto px-6 pt-5">
                    <div data-testid="payment-status-banner" className="p-3.5 rounded-[10px] bg-[#06D6A0]/10 border border-[#06D6A0]/30 text-sm text-[#05A37E] font-semibold flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" /> {paymentMsg}
                    </div>
                </div>
            )}

            {(
                <>
                    <section className="bg-[#0A2540] text-white relative overflow-hidden">
                        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(ellipse 50% 70% at 75% 20%, #00B4D8 0%, transparent 60%)" }} />
                        <div className="relative max-w-6xl mx-auto px-6 py-14 text-center">
                            <div className="inline-block text-[10px] tracking-[0.25em] uppercase font-bold text-[#7CD4EA] border border-[#7CD4EA]/30 rounded-full px-4 py-1.5">{t("portal.hero_tag")}</div>
                            <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold font-heading tracking-tight">{t("portal.hero_title")}</h1>
                            <p className="mt-2.5 text-slate-300 max-w-xl mx-auto text-sm">{t("portal.hero_desc2")}</p>
                            <form className="mt-7 max-w-xl mx-auto flex gap-2 bg-white rounded-full p-1.5 shadow-xl" onSubmit={(e) => { e.preventDefault(); search(); }}>
                                <div className="flex items-center gap-2 flex-1 pl-4">
                                    <Search className="w-4 h-4 text-slate-400 shrink-0" />
                                    <input data-testid="portal-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                                        placeholder={t("portal.search_ph")} className="flex-1 py-2.5 text-sm text-[#0A2540] focus:outline-none bg-transparent min-w-0" />
                                </div>
                                <button type="submit" data-testid="portal-search-btn" className="px-6 py-2.5 rounded-full bg-[#00B4D8] hover:bg-[#00c3eb] text-white text-sm font-bold transition-colors shrink-0">{t("portal.search_btn")}</button>
                            </form>
                        </div>
                    </section>

                    <section className="max-w-6xl mx-auto px-6 py-8">
                        <div className="flex flex-wrap items-center gap-2.5 mb-5" data-testid="portal-filters">
                            {STATUSES.map((s) => (
                                <button key={s} data-testid={`portal-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setStatusF(s)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${statusF === s ? "bg-[#0A2540] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#00B4D8]"}`}>
                                    {s !== "All" && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[s] }} />}
                                    {tr(s)}
                                </button>
                            ))}
                            <select data-testid="portal-type-filter" value={typeF} onChange={(e) => setTypeF(e.target.value)}
                                className="ml-auto rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00B4D8]">
                                {TYPES.map((t) => <option key={t} value={t}>{tr(t)}</option>)}
                            </select>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                            <div className="lg:col-span-7 space-y-3" data-testid="portal-results">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-[0.14em]">{filtered.length} {t(filtered.length !== 1 ? "portal.result_many" : "portal.result_one")}</div>
                                {filtered.map((a) => (
                                    <motion.button key={a.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                        data-testid={`portal-result-${a.file_number}`} onClick={() => openApplication(a)}
                                        className={`w-full text-left bg-white rounded-[14px] border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all border-l-4 ${selected?.id === a.id ? "border-[#00B4D8] ring-1 ring-[#00B4D8]" : "border-white/70"}`}
                                        style={{ borderLeftColor: STATUS_COLORS[a.status] || "#94A3B8" }}>
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <span className="font-data text-xs text-teal font-bold">{a.file_number}</span>
                                            <StatusBadge status={a.status} />
                                        </div>
                                        <div className="font-heading font-bold mt-1.5">{a.title}</div>
                                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" />{a.address} · {a.ward}</div>
                                        <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                                            <span className="font-semibold text-slate-500">{a.type}</span>
                                            <span className="font-data">{tr("Submitted")} {dstr(a.submitted_date)}</span>
                                        </div>
                                    </motion.button>
                                ))}
                                {filtered.length === 0 && <div className="text-sm text-slate-400 py-12 text-center bg-white rounded-[14px] border border-white/70">{t("portal.no_results")}</div>}
                            </div>

                            <div className="lg:col-span-5 space-y-5">
                                <div className="bg-white rounded-[18px] border border-white/70 shadow-sm p-6 sticky top-24" data-testid="portal-map-card">
                                    <h3 className="font-heading font-bold text-lg mb-1">{tr("Map of Active Applications")}</h3>
                                    <p className="text-xs text-slate-400 mb-4">{tr("Parcels are colour-coded by file status. Click a parcel or a result to view the public record.")}</p>
                                    {home && <MapPanel applications={home.applications} onSelect={(a) => { const full = results.find((r) => r.id === a.id); if (full) openApplication(full); }} className="h-[400px] border border-slate-200/70" />}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Public record overlay — opens in place instead of replacing the map */}
                    <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
                        <DialogContent className="sm:max-w-2xl w-[94vw] max-h-[86vh] overflow-y-auto rounded-[18px]" data-testid="portal-detail-overlay">
                            {selected && (
                                <div data-testid="portal-detail">
                                    <button data-testid="portal-back-to-map" onClick={() => setSelected(null)}
                                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-400 hover:text-[#0092B0] transition-colors mb-3">
                                        <ArrowLeft className="w-3.5 h-3.5" /> {t("portal.back_map")}
                                    </button>
                                    <div className="flex items-center gap-2.5 flex-wrap">
                                        <span className="font-data text-sm text-teal font-bold">{selected.file_number}</span>
                                        <StatusBadge status={selected.status} size="md" />
                                    </div>
                                    <h2 className="font-heading font-bold text-xl mt-2">{selected.title}</h2>
                                    <div className="text-xs text-slate-500 mt-1">{selected.address} · {selected.ward} · {selected.type}</div>

                                    <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mt-6 mb-3">{tr("Public timeline")}</h3>
                                    {(selected.events || []).map((e, i) => (
                                        <div key={e.id} className="flex gap-3">
                                            <div className="flex flex-col items-center">
                                                <div className={`w-3 h-3 rounded-full border-2 ${e.status === "completed" ? "bg-[#06D6A0] border-[#06D6A0]" : e.status === "current" ? "bg-[#FFB703] border-[#FFB703]" : "bg-transparent border-slate-300"}`} />
                                                {i < selected.events.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                                            </div>
                                            <div className="pb-4">
                                                <div className="text-sm font-semibold">{e.name}</div>
                                                <div className="text-xs text-slate-400 font-data">{dstr(e.date)}</div>
                                            </div>
                                        </div>
                                    ))}

                                    {(selected.documents || []).length > 0 && (
                                        <>
                                            <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mt-4 mb-3">{tr("Public documents")}</h3>
                                            <div className="space-y-2" data-testid="portal-documents">
                                                {selected.documents.map((d) => (
                                                    <div key={d.id} className="flex items-center justify-between gap-2 p-3 rounded-[10px] bg-[#F8FAFC] border border-slate-100" data-testid={`portal-doc-${d.id.slice(0, 8)}`}>
                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                            <FileText className="w-4 h-4 text-teal shrink-0" />
                                                            <span className="text-xs font-semibold truncate">{d.name}</span>
                                                        </div>
                                                        {d.downloadable ? (
                                                            <a href={`${API}/documents/${d.id}/download`} data-testid={`portal-doc-download-${d.id.slice(0, 8)}`}
                                                                className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-[#0092B0] hover:text-[#00B4D8] border border-[#00B4D8]/30 rounded-full px-2.5 py-1 transition-colors">
                                                                <Download className="w-3 h-3" /> {d.size}
                                                            </a>
                                                        ) : (
                                                            <span className="text-[10px] font-data text-slate-400 shrink-0">{d.size}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    <div className="mt-6 p-4 rounded-[12px] bg-[#0A2540] text-white flex items-center justify-between gap-3" data-testid="portal-applicant-cta">
                                        <div className="text-xs leading-relaxed text-slate-300"><Lock className="w-3.5 h-3.5 inline mr-1 text-[#7CD4EA]" />{tr("Applicant? Pay fees, submit and track from the Applicant Portal.")}</div>
                                        <Link to="/developer" className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] bg-[#00B4D8] hover:bg-[#00c3eb] text-white text-xs font-bold transition-colors">{tr("Sign in")}</Link>
                                    </div>
                                </div>
                            )}
                        </DialogContent>
                    </Dialog>
                </>
            )}

            {(
                <section className="max-w-6xl mx-auto px-6 pb-10" data-testid="portal-hearings">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                        <div>
                            <h1 className="font-heading font-bold text-2xl">{tr("Public Hearings")}</h1>
                            <p className="text-sm text-slate-500 mt-1">{t("portal.hearings_sub")}</p>
                        </div>
                        <div className="flex gap-1 bg-white rounded-full p-1 border border-slate-200">
                            <button data-testid="hearings-list-view" onClick={() => setHearView("list")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${hearView === "list" ? "bg-[#0A2540] text-white" : "text-slate-500"}`}><List className="w-3.5 h-3.5" /> {tr("List")}</button>
                            <button data-testid="hearings-calendar-view" onClick={() => setHearView("calendar")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${hearView === "calendar" ? "bg-[#0A2540] text-white" : "text-slate-500"}`}><CalendarDays className="w-3.5 h-3.5" /> {tr("Calendar")}</button>
                        </div>
                    </div>

                    {hearView === "list" ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {hearings.map((h, i) => (
                                <div key={i} data-testid={`portal-hearing-${i}`} className="bg-white rounded-[16px] border border-white/70 border-l-4 p-5 shadow-sm hover:shadow-md transition-shadow" style={{ borderLeftColor: "#FFB703" }}>
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-[#B8860B]"><CalendarDays className="w-3.5 h-3.5" /> {dstr(h.date)}</div>
                                    <div className="font-data font-bold mt-2.5">{h.file_number}</div>
                                    <div className="text-sm font-semibold mt-1">{h.title}</div>
                                    <div className="text-xs text-slate-500 mt-1">{h.address} · {h.type}</div>
                                </div>
                            ))}
                            {hearings.length === 0 && <div className="text-sm text-slate-400">{t("portal.no_hearings")}</div>}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                            <div className="lg:col-span-5"><MiniCalendar hearings={hearings} onPick={setCalPick} /></div>
                            <div className="lg:col-span-7">
                                <div className="bg-white rounded-[16px] border border-white/70 p-6 shadow-sm" data-testid="cal-day-detail">
                                    <h3 className="font-heading font-bold">{calPick ? `${t("portal.hearings")} — ${dstr(calPick[0]?.date)}` : t("portal.select_day")}</h3>
                                    <div className="mt-4 space-y-3">
                                        {(calPick || []).map((h, i) => (
                                            <div key={i} className="p-4 rounded-[12px] bg-[#F8FAFC] border border-slate-100">
                                                <div className="font-data text-xs text-teal font-bold">{h.file_number}</div>
                                                <div className="text-sm font-semibold mt-0.5">{h.title}</div>
                                                <div className="text-xs text-slate-500 mt-0.5">{h.address} · {h.type}</div>
                                            </div>
                                        ))}
                                        {!calPick && <p className="text-sm text-slate-400">{t("portal.cal_hint")}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            )}

            <footer className="bg-white border-t border-slate-200 mt-8 py-6">
                <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
                    <span>{tr("© 2026 City of Brampton · Public register — open data, no account required")}</span>
                    <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {tr("Powered by LandLogic NexusOne")}</span>
                </div>
            </footer>
            <TourPlayer steps={TOURS.portal.steps} title={tr("Public register tour")} open={tourOpen} onClose={() => setTourOpen(false)} />
        </div>
    );
}
